import { useCallback, useEffect, useReducer, useRef } from "react";
import {
  isResourceStale,
  loadResource,
  mutateResource,
  peekResource,
  subscribeResource,
} from "../lib/resourceCache.js";

/** Identidad estable para el `[]` por defecto — un array nuevo en cada render
 *  invalidaría los `useMemo` de `useTableState` en cada pasada. */
const EMPTY_ARRAY = Object.freeze([]);

/**
 * useResource — lectura cacheada con *stale-while-revalidate*.
 *
 * Sustituye al patrón `useState + useEffect + setLoading(true)` que repetían
 * todas las listas de /admin. La diferencia que se nota: al volver a una
 * pantalla ya visitada la tabla aparece PINTADA, y la revalidación (si toca)
 * ocurre por detrás sin tapar nada.
 *
 *   const { data, loading, revalidating, error, refresh } =
 *     useResource("fleet:vehicles", api.getVehicles, { fallback: [] });
 *
 * @param {string} key — clave de caché (`fleet:vehicles`, `fleet:groups`, …).
 * @param {() => Promise<any>} fetcher — no hace falta memoizarlo: se lee por
 *   ref, así que una función inline no dispara peticiones extra.
 * @param {object} [opts]
 * @param {number} [opts.ttlMs=30000] — cuánto vale el dato antes de revalidar.
 *   30 s: suficiente para que ir y volver entre pantallas sea instantáneo, y
 *   corto para que una edición hecha en otra pestaña no se quede pegada.
 * @param {boolean} [opts.enabled=true] — false congela el hook (no lee, no pide).
 * @param {any} [opts.fallback] — valor mientras no hay dato. Un `[]` literal
 *   es seguro: se normaliza a una constante compartida.
 *
 * @returns {{data:any, loading:boolean, revalidating:boolean, error:Error|null,
 *   refresh:() => Promise<any>, setData:(next:any) => void}}
 *   - `loading` es solo la PRIMERA carga (aún no hay nada que enseñar). Una
 *     revalidación con dato en mano no lo activa — eso es `revalidating`.
 */
export function useResource(
  key,
  fetcher,
  { ttlMs = 30_000, enabled = true, fallback } = {},
) {
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const [, rerender] = useReducer((n) => n + 1, 0);

  useEffect(() => {
    if (!key || !enabled) return undefined;
    return subscribeResource(key, rerender);
  }, [key, enabled]);

  const entry = key ? peekResource(key) : undefined;
  const version = entry?.version ?? -1;

  const revalidate = useCallback(() => {
    loadResource(key, () => fetcherRef.current()).catch(() => {
      /* el error queda en la entrada; lo lee el render */
    });
  }, [key]);

  // (1) Al montar y al cambiar de clave: si el dato venció (o no existe), se
  // pide. `ttlMs` queda FUERA de las dependencias a propósito — es un umbral
  // para decidir, no un evento que deba disparar una petición.
  useEffect(() => {
    if (!key || !enabled) return;
    if (isResourceStale(key, ttlMs)) revalidate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, enabled, revalidate]);

  // (2) Invalidación explícita mientras la pantalla está montada (alguien
  // guardó algo en otra pantalla y marcó esta lista).
  //
  // La condición NO es «está stale» sino «está stale POR invalidación»
  // (`fetchedAt === 0`) y además ya había dato. La diferencia es lo que separa
  // un hook de un bucle: con `ttlMs` bajo, «revalida si está stale» dispara
  // otra petición en cuanto la anterior termina —porque terminar es una
  // notificación, y la notificación vuelve a correr el efecto— y así para
  // siempre. Exigir `data !== undefined` deja fuera el otro caso de
  // `fetchedAt === 0`: el vaciado por cierre de sesión, que no debe provocar
  // una petición en nombre de un usuario que ya se fue.
  useEffect(() => {
    if (!key || !enabled) return;
    const current = peekResource(key);
    if (!current || current.promise) return;
    if (current.fetchedAt === 0 && current.data !== undefined) revalidate();
  }, [key, enabled, revalidate, version]);

  const refresh = useCallback(() => {
    if (!key) return Promise.resolve(undefined);
    return loadResource(key, () => fetcherRef.current(), { force: true }).catch(
      () => undefined,
    );
  }, [key]);

  const setData = useCallback(
    (next) => {
      if (key) mutateResource(key, next);
    },
    [key],
  );

  const hasData = entry?.data !== undefined;
  const normalizedFallback =
    Array.isArray(fallback) && fallback.length === 0 ? EMPTY_ARRAY : fallback;

  return {
    data: hasData ? entry.data : normalizedFallback,
    // `hasData` separa los dos fracasos que NO son el mismo: «la primera carga
    // falló, no hay nada que enseñar» (la pantalla se reemplaza por el error)
    // y «la comprobación falló pero lo de antes sigue en pantalla» (el error
    // va al margen). Sin este dato la página no puede distinguirlos y acaba
    // tirando una tabla buena por un fallo de fondo.
    hasData,
    loading: enabled && !hasData && !entry?.error,
    revalidating: Boolean(entry?.promise),
    error: entry?.error ?? null,
    refresh,
    setData,
  };
}

export default useResource;
